import React, { useRef, useState } from 'react'
import { Box, Button, Flex, Image, Input, Spinner } from '@chakra-ui/core'
import { AiOutlineCamera } from 'react-icons/ai'
import { RiImageAddLine, RiArrowGoBackFill, RiCloseFill } from 'react-icons/ri'
import BaseModal from './BaseModal'
import { PreviewFile } from 'config/types'
import colors from 'constants/colors'
import { isRtlSelector } from 'redux/selectors/general'
import { useSelector } from 'react-redux'
import { useCamera } from 'hooks/useCamera'
import { PublishButton } from './PublishButton'
import { CustomTextArea } from 'components/helperStyles'
import useLocaleText from 'components/useLocaleText'
import { isVideo } from './MediaViewer/utils'
import { VideoPreview } from './VideoPreview'
import FilePreview from './FilePreview'
type EnhancedPreviewFile = PreviewFile & {
  forceFileType?: boolean
}

interface IProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (params: { commentText?: string; data: PreviewFile[] }) => void
  onRemove?: (index: number) => void
  isLoading?: boolean
  initialPreview?: PreviewFile[]
  isCameraMode?: boolean
  initialCommentText?: string
}

const MediaModal = ({
  isOpen,
  onClose,
  onSubmit,
  onRemove,
  isLoading = false,
  initialPreview = [],
  isCameraMode = false,
  initialCommentText = '',
}: IProps) => {
  const isRtl = useSelector(isRtlSelector)

  const [commentText, setCommentText] = useState(initialCommentText)
  const publishBtnRef = useRef<HTMLButtonElement>(null)
  const t_write_a_comment = useLocaleText('write_a_comment')

  const {
    cameraIsLoading,
    imagePreview,
    imageFiles,
    videoRef,
    canvasRef,
    inputRef,
    takePhoto,
    handleChangeEvent,
    selectImage,
    removeImage,
    resetCamera,
    stopCamera,
    setCameraIsLoading,
  } = useCamera(isOpen && isCameraMode)

  const displayFiles = isCameraMode ? [] : initialPreview

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value)
  }

  const handleSubmit = () => {
    if (isCameraMode) {
      const previewFiles = imageFiles.map((file, index) => ({
        file,
        previewUrl: imagePreview[index],
      }))
      onSubmit({ data: previewFiles, commentText })
    } else {
      onSubmit({ data: displayFiles, commentText })
    }
    setCommentText('')
  }

  const handleRemoveFile = (index: number) => {
    if (isCameraMode) {
      removeImage(index)
    } else if (onRemove) {
      onRemove(index)
    }
  }

  const onEnter = (e: React.KeyboardEvent) => {
    if (document.activeElement && e.key === 'Enter' && !e.shiftKey) {
      handleSubmit()
      return true
    }
  }

  const closeModalHandler = () => {
    if (isCameraMode) {
      setCameraIsLoading(true)
      stopCamera()
      removeImage()
    }
    onClose()
  }

  const handleBackButton = () => {
    if (isCameraMode && imagePreview.length) {
      resetCamera()
    } else {
      closeModalHandler()
    }
  }

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={closeModalHandler}
        closeOnEsc
        headerContent={
          <Button
            justifySelf="flex-start"
            variant="ghost"
            position="absolute"
            left={!isRtl ? '10px' : 'auto'}
            right={isRtl ? '10px' : 'auto'}
            top="8px"
            borderRadius="full"
            onClick={handleBackButton}
            width="36px"
            height="36px"
            background={colors.greyMedium}
            p="0"
            minWidth={0}
          >
            <RiArrowGoBackFill size="12px" />
          </Button>
        }
        bodyContent={
          <Box
            mt="15px"
            maxHeight="400px"
            overflowY="auto"
            pointerEvents={isLoading ? 'none' : 'auto'}
            opacity={isLoading ? 0.5 : 1}
          >
            <Flex
              height="100%"
              minHeight="300px"
              justifyContent="center"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: '10px' }}
            >
              {isCameraMode ? (
                <>
                  {cameraIsLoading ? (
                    <Flex alignItems="center" justifyContent="center" height="350px" width="100%">
                      <Spinner size="xl" />
                    </Flex>
                  ) : null}

                  <video
                    autoPlay
                    ref={videoRef}
                    style={{
                      height: '350px',
                      width: '100%',
                      objectFit: 'cover',
                      display: !cameraIsLoading && !imagePreview.length ? 'block' : 'none',
                    }}
                  />

                  {!cameraIsLoading &&
                    imagePreview.length > 0 &&
                    imagePreview.map((preview, index) => (
                      <Box key={index} position="relative">
                        <Image width="150px" height="150px" objectFit="cover" src={preview} />
                        <Button
                          position="absolute"
                          top="5px"
                          right="5px"
                          size="sm"
                          onClick={() => handleRemoveFile(index)}
                          background={colors.greyMedium}
                          borderRadius="full"
                          p="0"
                          minWidth="24px"
                          height="24px"
                        >
                          <RiCloseFill size="12px" />
                        </Button>
                      </Box>
                    ))}
                </>
              ) : (
                displayFiles.map((file, index) => {
                  if (!file) return null

                  const enhancedFile = file as EnhancedPreviewFile

                  return (
                    <Box key={index} position="relative">
                      {isVideo(file.file.type) ? (
                        <Flex
                          borderRadius="10px"
                          width="150px"
                          height="150px"
                          background={colors.greyLight}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <VideoPreview url={file.previewUrl} />
                        </Flex>
                      ) : file.file.type.startsWith('image') && !enhancedFile.forceFileType ? (
                        <Image
                          borderRadius="10px"
                          width="150px"
                          height="150px"
                          objectFit="cover"
                          src={file.previewUrl}
                          alt={`Preview ${index + 1}`}
                        />
                      ) : (
                        <FilePreview fileUrl={file.previewUrl} width="150px" height="150px" />
                      )}

                      <Button
                        position="absolute"
                        top="5px"
                        right="5px"
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                        background={colors.greyMedium}
                        borderRadius="full"
                        p="0"
                        minWidth="24px"
                        height="24px"
                      >
                        <RiCloseFill size="12px" />
                      </Button>
                    </Box>
                  )
                })
              )}
            </Flex>
            {isCameraMode && <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>}
          </Box>
        }
        footerContent={
          <Flex justifyContent="space-between" alignItems="center" w="100%" style={{ gap: '10px' }}>
            {(!isCameraMode && displayFiles.length > 0) || (isCameraMode && imagePreview.length > 0) ? (
              <>
                <CustomTextArea
                  placeholder={t_write_a_comment}
                  name="comment"
                  value={commentText}
                  disabled={isLoading}
                  onChange={handleTextAreaChange}
                  onKeyPress={onEnter}
                  maxRows={5}
                  minRows={1}
                  height="40px"
                />
                <PublishButton
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  isDisabled={isCameraMode ? imagePreview.length === 0 : displayFiles.length === 0}
                  tabIndex={0}
                  ref={publishBtnRef}
                />
              </>
            ) : (
              isCameraMode && (
                <Flex width="100%" justifyContent="center" mb="3" alignItems="center" style={{ gap: '20px' }}>
                  <Button
                    onClick={takePhoto}
                    variant="outline"
                    borderRadius="full"
                    textTransform="uppercase"
                    width="50px"
                    height="50px"
                    p="5px"
                    tabIndex={1}
                  >
                    <AiOutlineCamera fontSize="25px" />
                  </Button>
                  <Button
                    variant="outline"
                    borderRadius="full"
                    textTransform="uppercase"
                    width="50px"
                    height="50px"
                    p="5px"
                    onClick={selectImage}
                  >
                    <RiImageAddLine />
                  </Button>
                </Flex>
              )
            )}
          </Flex>
        }
      />
      {isCameraMode && (
        <Input
          position="absolute"
          width="0"
          height="0"
          type="file"
          ref={inputRef}
          capture="environment"
          accept="image/*"
          multiple
          onChange={handleChangeEvent}
          visibility="hidden"
        />
      )}
    </>
  )
}

export default MediaModal
